import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity, BarChart3, Building2, Car, ClipboardCheck, DollarSign, ExternalLink, HeadphonesIcon, LogOut,
  Menu, Phone, ScrollText, ShieldCheck, Users, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: BarChart3, exact: true, testId: "admin-nav-dashboard" },
  { to: "/admin/inventory", label: "Inventory", icon: Car, testId: "admin-nav-inventory" },
  { to: "/admin/customers", label: "Customers", icon: Users, testId: "admin-nav-customers" },
  { to: "/admin/leads", label: "Leads", icon: ClipboardCheck, testId: "admin-nav-leads" },
  { to: "/admin/dealers", label: "Dealers", icon: Building2, testId: "admin-nav-dealers" },
  { to: "/admin/bdc", label: "BDC Team", icon: HeadphonesIcon, testId: "admin-nav-bdc" },
  { to: "/admin/users", label: "All Users", icon: ShieldCheck, testId: "admin-nav-users" },
  { to: "/admin/phone", label: "Phone", icon: Phone, testId: "admin-nav-phone" },
  { to: "/admin/billing", label: "Billing", icon: DollarSign, testId: "admin-nav-billing" },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText, testId: "admin-nav-audit" },
];

const linkClass = ({ isActive }) =>
  `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
    isActive
      ? "bg-gold/15 text-gold border border-gold/30"
      : "text-slate-300 hover:bg-slate-800 hover:text-white"
  }`;

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleSignOut = async () => {
    await logout();
    navigate("/login");
  };

  const initials = useMemo(() => {
    const n = user?.full_name || user?.email || "AD";
    return n.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  }, [user]);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-shell">
      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between bg-slate-950 text-white px-4 h-14 sticky top-0 z-30">
        <button aria-label="Open sidebar" onClick={() => setOpen(true)} className="text-white/80" data-testid="admin-sidebar-open-btn">
          <Menu size={20} />
        </button>
        <span className="font-display font-semibold tracking-wide">Belomotors <span className="text-gold text-xs">ADMIN</span></span>
        <button onClick={handleSignOut} aria-label="Sign out" className="text-white/80" data-testid="admin-signout-mobile-btn">
          <LogOut size={17} />
        </button>
      </div>

      <aside
        className={`fixed lg:fixed top-0 left-0 z-40 h-screen w-64 shrink-0 bg-slate-950 text-slate-100 flex flex-col transform transition-transform lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <Link to="/admin" className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
            Belomotors
            <span className="inline-flex items-center gap-1 rounded-full border border-gold/50 text-gold bg-gold/10 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold">
              <ShieldCheck size={11} /> Admin
            </span>
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden text-slate-400 hover:text-white" aria-label="Close sidebar">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, exact, testId }) => (
            <NavLink key={to} to={to} end={exact} className={linkClass} data-testid={testId}>
              <Icon size={16} className="shrink-0" />
              {label}
            </NavLink>
          ))}
          {/* Phase 11 — "View Site" jumps to the public marketplace in a new
              tab so admins can spot-check what customers see without losing
              their portal session. */}
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition"
            data-testid="admin-nav-view-site"
          >
            <ExternalLink size={16} className="shrink-0" />
            View site
          </a>
        </nav>

        <div className="mt-auto border-t border-slate-800 px-4 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gold/20 text-gold flex items-center justify-center font-display font-semibold text-sm">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.full_name || "Admin"}</p>
            <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleSignOut}
            className="text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-slate-800"
            aria-label="Sign out"
            data-testid="admin-signout-btn"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 z-30 bg-slate-900/60 lg:hidden" onClick={() => setOpen(false)} aria-hidden="true" />
      )}

      <div className="lg:pl-64">
        <main className="min-h-screen" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
};

export default AdminLayout;

export const AdminPage = ({ title, subtitle, action, children, testId }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid={testId}>
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
          <Activity size={12} /> Admin console
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);
